/**
 * Semantic tones and the single status/priority → tone mapping
 * (DESIGN_SYSTEM.md §2.3–2.4). Every status badge in the app should resolve
 * its colour through here instead of a local map.
 */
export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brand' | 'violet';

/** Soft tint background + dark text. Never a saturated fill. The `badge-*` fills are
 *  one step richer than -50 so a status reads on the white panels (tokens.css). */
export const TONE_BADGE: Record<Tone, string> = {
  neutral: 'bg-badge-neutral text-surface-700',
  info: 'bg-badge-info text-info-800',
  success: 'bg-badge-success text-success-700',
  warning: 'bg-badge-warning text-warning-800',
  danger: 'bg-badge-danger text-danger-700',
  brand: 'bg-badge-brand text-primary-800',
  violet: 'bg-badge-violet text-violet-800',
};

/** Solid dot / icon colour for the same tone. */
export const TONE_DOT: Record<Tone, string> = {
  neutral: 'bg-surface-400',
  info: 'bg-info-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
  brand: 'bg-primary-500',
  violet: 'bg-violet-500',
};

/**
 * Outline variant (clarity round, D-22): a hairline and tinted text, no fill, so a
 * priority tag never reads as a status (statuses keep the soft fill).
 */
export const TONE_OUTLINE: Record<Tone, string> = {
  neutral: 'border-border text-text-2',
  info: 'border-info-300 text-info-800',
  success: 'border-success-300 text-success-700',
  warning: 'border-warning-300 text-warning-800',
  danger: 'border-danger-300 text-danger-700',
  brand: 'border-primary-300 text-primary-800',
  violet: 'border-violet-300 text-violet-800',
};

export const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-surface-600',
  info: 'text-info-700',
  success: 'text-success-700',
  warning: 'text-warning-700',
  danger: 'text-danger-700',
  brand: 'text-primary-700',
  violet: 'text-violet-700',
};

/**
 * P-05/B (D-54) — the five task / milestone statuses have ONE colour each, the
 * same in the status circles and in every status badge:
 * ⚪ בתכנון · 🔵 בביצוע · 🟣 בהתקדמות · 🟠 בבדיקה · 🟢 הושלם.
 */
export const WORK_STATUS_TONE: Readonly<Record<'planning' | 'in_progress' | 'in_review' | 'testing' | 'completed', Tone>> = {
  planning: 'neutral',
  in_progress: 'brand',
  in_review: 'violet',
  testing: 'warning',
  completed: 'success',
};

const STATUS_TONE: Record<string, Tone> = {
  // Not started / draft / archived
  draft: 'neutral', planning: 'neutral', pending: 'neutral', closed: 'neutral',
  cancelled: 'neutral', canceled: 'neutral', superseded: 'neutral', inactive: 'neutral', archived: 'neutral',
  locked: 'neutral',
  // In motion (owned by us or with the customer)
  open: 'info', new: 'info', in_progress: 'info', sent: 'info', viewed: 'info', ready_to_sign: 'info',
  employee_approved: 'info', testing: 'info', scheduled: 'info', submitted: 'info', running: 'info',
  // Needs attention / waiting / paused
  waiting: 'warning', waiting_customer: 'warning', on_hold: 'warning', in_review: 'warning', pending_signature: 'warning',
  pending_employee: 'warning', pending_approval: 'warning', expired: 'warning', partial: 'warning',
  partially_paid: 'warning', paused: 'warning', at_risk: 'warning',
  // Done, positive, healthy
  completed: 'success', resolved: 'success', accepted: 'success', signed: 'success', approved: 'success',
  ote_approved: 'success', report_emitted: 'success', active: 'success', paid: 'success', done: 'success',
  delivered: 'success', converted: 'success', won: 'success', succeeded: 'success', success: 'success',
  // Failed / negative outcome
  rejected: 'danger', terminated: 'danger', failed: 'danger', overdue: 'danger', breached: 'danger',
  lost: 'danger', error: 'danger',
};

export function statusTone(status: string | null | undefined): Tone {
  if (status && status in WORK_STATUS_TONE) return WORK_STATUS_TONE[status as keyof typeof WORK_STATUS_TONE];
  return (status && STATUS_TONE[status]) || 'neutral';
}

/** Priority: urgent → danger, high → warning, medium/low → neutral. Brand is never a priority colour. */
export function priorityTone(priority: string | null | undefined): Tone {
  switch (priority) {
    case 'urgent':
    case 'critical':
      return 'danger';
    case 'high':
      return 'warning';
    default:
      return 'neutral';
  }
}
